import { parallelLimit } from "async-es";

/**
 * Creates an RTK Query `queryFn` that splits a list of items into chunks,
 * runs requests in parallel, and merges the results.
 *
 * @param {object} config
 * @param {(args: any) => any[]} config.getChunkSource - Extracts the item list from query args.
 * @param {(chunk: any[], args: any) => object} config.query - Returns baseQuery args for a chunk. Receives the full query args as the second parameter.
 * @param {(result: any) => any[]} config.getData - Extracts the data array from a single chunk result.
 * @param {number} [config.chunkSize=10]
 * @param {number} [config.concurrency=2]
 */
export function createChunkedQueryFn({
  getChunkSource,
  query,
  getData,
  chunkSize = 10,
  concurrency = 2,
}) {
  return (args, api, extraOptions, baseQuery) => {
    const items = [...getChunkSource(args)].sort();
    const chunks = Array.from({ length: Math.ceil(items.length / chunkSize) }, (_, i) =>
      items.slice(i * chunkSize, (i + 1) * chunkSize),
    );

    return new Promise((resolve) => {
      parallelLimit(
        chunks.map((chunk) => (cb) => {
          baseQuery(query(chunk, args), api, extraOptions)
            .then((result) => {
              if (result.error) cb(result.error);
              else cb(null, result);
            })
            .catch((err) => cb(err));
        }),
        concurrency,
        (err, results) => {
          if (err) {
            resolve({ error: err });
          } else {
            resolve({ data: results.flatMap((r) => getData(r)) });
          }
        },
      );
    });
  };
}
